// "Jun 12, 2026, 01:04 AM" — matches aistudio's UserTimestampInfoBlock tooltip style
export function formatAbsoluteTime(epochMs: string | number): string {
  const ms = typeof epochMs === 'string' ? Number(epochMs) : epochMs;
  if (!Number.isFinite(ms) || ms <= 0) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(ms));
}

// Environment labels are no longer a static map: EP bots name their own
// pipeline stages, so labels come from `useCalibrationEnvironments().getEnvLabel`.

function formatClockTime(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/**
 * 'Today' / 'Tomorrow' / a short date, relative to the current calendar day —
 * mirrors aistudio's TableView.vue `getDayLabel`, used by the SCHEDULED
 * status tooltip's listening-window and analysis-start-time lines.
 */
export function getScheduledDayLabel(date: Date, lowercase = false): string {
  const today = new Date();
  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (isSameDay(date, today)) return lowercase ? 'today' : 'Today';
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  if (isSameDay(date, tomorrow)) return lowercase ? 'tomorrow' : 'Tomorrow';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * "Today, 14:30 – 15:30" — a scheduled run's listening window, for the
 * SCHEDULED status tooltip. Mirrors aistudio's `formatListeningWindow`.
 */
export function formatListeningWindow(
  startTime: string | null | undefined,
  endTime: string | null | undefined
): string {
  if (!startTime || !endTime) return '-';
  const start = new Date(startTime);
  const end = new Date(endTime);
  return `${getScheduledDayLabel(start)}, ${formatClockTime(start)} – ${formatClockTime(end)}`;
}

/**
 * "~16:00 today" — analysis is expected to start 30 minutes after the
 * listening window closes. Mirrors aistudio's `formatAnalysisStartTime`.
 */
export function formatAnalysisStartTime(endTime: string | null | undefined): string {
  if (!endTime) return '-';
  const analysisTime = new Date(new Date(endTime).getTime() + 30 * 60 * 1000);
  return `~${formatClockTime(analysisTime)} ${getScheduledDayLabel(analysisTime, true)}`;
}
